import { FC, useCallback, useState } from 'react';
import NotificationShortCodePicker from '../../components/NotificationShortCodePicker/NotificationShortCodePicker';
import { INotificationSetting } from '../../types/notifications';
import { LOCALES, maxPushNotificationMessageLength, maxPushNotificationSubjectLength } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import MultiLanguageTextArea from 'src/view/components/Controls/MultiLanguageTextArea/MultiLanguageTextArea';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { addShortCode } from '~/helpers';
import { ILangTextValidation, TTranslate } from '~/types/main';
import './style.scss';

type TValue = Pick<INotificationSetting, 'subject' | 'message'>;

interface IProps {
  shortCodePickerOptions: IPickerOption[];
  subject: TTranslate;
  message: TTranslate;
  subjectValidation: ILangTextValidation;
  messageValidation: ILangTextValidation;
  changeNotification: (value: Partial<TValue>) => void;
}

const NotificationSettingEditMainBlock: FC<IProps> = ({ shortCodePickerOptions, subject, message, ...props }: IProps): JSX.Element => {
  const [currentLang, setCurrentLang] = useState<LOCALES>(localeStore.language === LOCALES.AR ? LOCALES.AR : LOCALES.EN);

  const selectShortCode = useCallback(
    (options: IPickerOption[]) => {
      const value: string = addShortCode(options, message[currentLang], maxPushNotificationMessageLength);

      onChangeMessage(value, currentLang);
    },
    [message, currentLang]
  );

  const onChangeSubject = useCallback(
    (value: string, language: LOCALES) => {
      props.changeNotification({ subject: { ...subject, [language]: value } });
    },
    [subject]
  );

  const onChangeMessage = useCallback(
    (value: string, currentLanguage: LOCALES) => {
      props.changeNotification({ message: { ...message, [currentLanguage]: value } });
    },
    [message]
  );

  return (
    <div className="d-flex flex-column gap-10">
      <Text type={TEXT.SECTION_HEADER} text="text_message" className="mb-2" />
      <NotificationShortCodePicker options={shortCodePickerOptions} selectShortCode={selectShortCode} />
      <MultiLanguageInput
        enText={subject.en}
        arText={subject.ar}
        onChangeValue={onChangeSubject}
        validation={props.subjectValidation}
        placeholder={localeStore.t('hint_subject')}
        className="notification-setting-edit-main-block__input"
        maxLength={maxPushNotificationSubjectLength}
      />
      <MultiLanguageTextArea
        enText={message.en}
        arText={message.ar}
        onChangeValue={onChangeMessage}
        changeCurrentLanguageCallback={setCurrentLang}
        validation={props.messageValidation}
        placeholder={localeStore.t('hint_message')}
        showLength
        disableLineBreak
        className="notification-setting-edit-main-block__text-area"
        textAreaClassName="notification-setting-edit-main-block__text-area-input"
        maxLength={maxPushNotificationMessageLength}
      />
    </div>
  );
};

export default NotificationSettingEditMainBlock;
